/**
 * Ctrl+wheel zoom and pinch-zoom for touch devices.
 * Preserves zoom center so the point under the cursor stays fixed.
 *
 * Adapted from SharedVueComponents/Pdf/usePdfZoomHandler.js.
 *
 * @param {Object} options
 * @param {Function} options.getZoom  - () => current zoom number
 * @param {Function} options.setZoom  - (newZoom: number) => void
 * @param {number}   [options.minZoom=0.25]
 * @param {number}   [options.maxZoom=3]
 * @param {number}   [options.zoomStep=0.1]
 * @returns {{ attach(el: HTMLElement): void, detach(): void }}
 */
export function useScrollZoom(options) {
  const {
    getZoom,
    setZoom,
    minZoom = 0.25,
    maxZoom = 3,
    zoomStep = 0.1,
  } = options;

  let el = null;
  let rafId = null;

  // ---- zoom-center preservation ----

  function captureCenter(event) {
    const rect = el.getBoundingClientRect();
    const vx = event?.clientX != null ? event.clientX - rect.left : rect.width / 2;
    const vy = event?.clientY != null ? event.clientY - rect.top : rect.height / 2;
    return {
      scrollX: el.scrollLeft,
      scrollY: el.scrollTop,
      viewportX: vx,
      viewportY: vy,
      initialZoom: getZoom(),
    };
  }

  function restoreCenter(center) {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      rafId = null;
      const factor = getZoom() / center.initialZoom;
      el.scrollLeft = center.scrollX * factor + center.viewportX * (factor - 1);
      el.scrollTop = center.scrollY * factor + center.viewportY * (factor - 1);
    });
  }

  // ---- Ctrl + wheel ----

  function handleWheel(e) {
    if (!e.ctrlKey) return;
    e.preventDefault();

    const oldZoom = getZoom();
    const delta = e.deltaY > 0 ? -zoomStep : zoomStep;
    const newZoom = clampZoom(oldZoom + delta);
    if (newZoom === oldZoom) return;

    const center = captureCenter(e);
    setZoom(newZoom);
    restoreCenter(center);
  }

  // ---- pinch-zoom (touch) ----

  let initialDist = null;
  let initialPinchZoom = null;
  let lastPinchTime = 0;

  function dist(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function midpoint(touches) {
    return {
      clientX: (touches[0].clientX + touches[1].clientX) / 2,
      clientY: (touches[0].clientY + touches[1].clientY) / 2,
    };
  }

  function handleTouchStart(e) {
    if (e.touches.length === 2) {
      e.preventDefault();
      initialDist = dist(e.touches);
      initialPinchZoom = getZoom();
    }
  }

  function handleTouchMove(e) {
    if (e.touches.length !== 2 || initialDist == null) return;
    e.preventDefault();

    // Throttle to ~60 fps
    const now = performance.now();
    if (now - lastPinchTime < 16) return;
    lastPinchTime = now;

    const scale = dist(e.touches) / initialDist;
    const target = clampZoom(initialPinchZoom * scale);
    if (target === getZoom()) return;

    const center = captureCenter(midpoint(e.touches));
    setZoom(target);
    restoreCenter(center);
  }

  function handleTouchEnd(e) {
    if (e.touches.length < 2) {
      initialDist = null;
      initialPinchZoom = null;
    }
  }

  // ---- helpers ----

  function clampZoom(z) {
    return Math.min(maxZoom, Math.max(minZoom, +z.toFixed(2)));
  }

  // ---- public API ----

  function attach(scrollEl) {
    el = scrollEl;
    el.addEventListener('wheel', handleWheel, { passive: false });
    el.addEventListener('touchstart', handleTouchStart, { passive: false });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: false });
  }

  function detach() {
    if (!el) return;
    el.removeEventListener('wheel', handleWheel);
    el.removeEventListener('touchstart', handleTouchStart);
    el.removeEventListener('touchmove', handleTouchMove);
    el.removeEventListener('touchend', handleTouchEnd);
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
    el = null;
  }

  return { attach, detach };
}
